// SugarDB Suggest Page - allows users to suggest new entries to the database

import { useState, Component, useEffect } from "react"
import { Dropdown, Input, Icon, TextArea } from "semantic-ui-react"
import axios from "axios"
import './css/wholepagecard.css'

// helper functions from visualizer tool
import AutoSVG from "../components/visualizer-tool/automatic-svg/AutoSVG.js"
import {formatString} from "../components/visualizer-tool/glycan-interpreter/parser.js"

// components
import Counter from "../components/database/Counter"
import ConfirmationModal from "../components/database/admin-mode/ConfirmationModal.js"

export default function Suggest() {
    const hostingServer = `https://sugardb.cs.uct.ac.za/`;

    const [confirmationOpen, setConfirmationOpen] = useState(false);

    // form states
    const [name, setName] = useState('') // [state, setState]
    const [email, setEmail] = useState('')
    const [serotype, setSerotype] = useState('')
    const [casper, setCASPER] = useState('')
    const [NMR, setNMR] = useState('')
    const [speciesWhereFound, setSpeciesWhereFound] = useState('')
    const [antigenType, setAntigenType] = useState('')
    const [molecularType, setMolecularType] = useState('')  
    const [doi, setDOI] = useState('')
    const [refs, setRefs] = useState('')
    const [branched, setBranched] = useState('')
    const [repeatedUnits, setRepeatedUnits] = useState('')

    // form message states
    const [formMessage, setFormMessage] = useState('') // form message state
    const [submitted, setSubmitted] = useState(false)

    // dropdown data
    const selectData = ['K-antigen', 'O-antigen']

    // for required fields:
    const [isNameFilled, setIsNameFilled] = useState(true)
    const [isEmailFilled, setIsEmailFilled] = useState(true)
    const [isSerotypeFilled, setIsSerotypeFilled] = useState(true)
    const [isCASPERFilled, setIsCASPERFilled] = useState(true)
    const [isAntigenTypeFilled, setIsAntigenTypeFilled] = useState(true)
    const [isDOIFilled, setIsDOIFilled] = useState(true)

    // state for carbuilder format
    const [casperClicked, setCASPERClicked] = useState(false)
    const [carbuilderFormat, setCarbuilderFormat] = useState('')

    // handle carbuilder format change and display it only when CASPER input is not clicked
    useEffect(() => {
        if (!casperClicked) {
            setCarbuilderFormat(formatString(casper))
        }
    }, [casperClicked])

    // handle CASPER input change, replace special characters
    const handleCASPERInputChange = (e) => {
        setCASPERClicked(true)
        let value = e.target.value
        value = value.replace(/->/g, '→').replace(/\\a/g, 'α').replace(/\\b/g, 'β');
        setCASPER(value);
    }

    // post suggestion data to backend
    const axiosPostData = async () => {
        setFormMessage('')
        const postData = {
            Name: name,
            Email: email,
            Serotype: serotype,
            Antigen_structure: casper,
            Carbuilder_structure_format: formatString(casper),
            NMR: NMR,
            Species_where_found: speciesWhereFound,
            Antigen_type: antigenType,
            Molecular_type: molecularType,
            DOI: doi,
            References: refs,
            Branched: branched,
            Repeat_unit_size: repeatedUnits
        }

        try {
            await axios.post(`${hostingServer}api/suggestions`, postData) 
            .then(res => setFormMessage(<p style={{color:"green"}}> { res.data }</p>))
            clearFields();
            setSubmitted(true);
            setConfirmationOpen(false);
        } catch (error) {
            alert("Error submitting entry suggestion. Please try again later.")
        }
    }

    // check if all required fields are filled, count errors
    const checkFields = () => {
        console.log(antigenType)
        var numErrors = 0
        // set error states for required fields
        if (name === '') { setIsNameFilled(false); numErrors++ } else { setIsNameFilled(true) }
        if (email === '') { setIsEmailFilled(false); numErrors++ } else { setIsEmailFilled(true) }
        if (serotype === '') { setIsSerotypeFilled(false); numErrors++ } else { setIsSerotypeFilled(true) }
        if (casper === '') { setIsCASPERFilled(false); numErrors++ } else { setIsCASPERFilled(true) }
        if (antigenType === '') { setIsAntigenTypeFilled(false); numErrors++ } else { setIsAntigenTypeFilled(true) }
        if (doi === '') { setIsDOIFilled(false); numErrors++ } else { setIsDOIFilled(true) }

        return numErrors
    }

    // clear all fields after submission
    const clearFields = () => {
        setName('')
        setEmail('')
        setSerotype('')
        setCASPER('')
        setNMR('')
        setSpeciesWhereFound('')
        setAntigenType('')
        setMolecularType('')
        setDOI('')
        setRefs('')
        setBranched('')
        setRepeatedUnits('')
    }

    // handle form submission: require user confrimation, check fields, post data, clear fields
    const handleSubmit = (e) => {
        e.preventDefault() // handle before the default action
        var numErrors = checkFields()
        
        if (numErrors > 0) {
            setFormMessage(<p className="required" style={{color:"red"}}>Please fill out all required fields.</p>)
        } else {
            setConfirmationOpen(true);
        }
    }

    // COMPONENT: dropdown for antigen type

    // options for dropdown
    const options = [
        { key: 1, text: 'Default', value: '' },
        ...selectData.map((item, index) => ({ key: index+2, text: item, value: item }))
    ]

    class AntigenTypeDropdown extends Component {

        state = { options }
        
        // add new antigen type to dropdown
        handleAddition = (e, { value }) => {
            this.setState((prevState) => ({
            options: [{ text: value, value }, ...prevState.options],
            }))
        }
        
        handleChange = (e, { value }) => this.setState({ currentValue: value })
        
        render() {
            return (
                <div className={isAntigenTypeFilled?"required field" : "error field"}>
                 <label>Antigen Type:</label>
                <Dropdown
                    options={this.state.options}
                    placeholder='Default'
                    search
                    selection
                    fluid
                    clearable
                    allowAdditions
                    value={antigenType}
                    onAddItem={this.handleAddition}
                    onChange={(e, {value}) => setAntigenType(value)}
                />
            </div>
            )
        }
    }
    
    return (
        <div className="entry-card" onClick={(e) => {setCASPERClicked(false)}}>
            <div className="entry-card-header" style={{color:'white',backgroundColor: submitted ? '#6D9886' : '#393E46'}}>
                {submitted ? <h1>Entry Submitted</h1> : <h1>New Entry Suggestion</h1>}
            </div>
           
            <form className="ui form" style={{padding:"5vh", height:'80vh', overflowY:'auto'}}>

                <div className="equal width fields" style={{marginBottom:"3vh"}}>
                    <div className={isNameFilled?"required field" : "error field"}>
                        <label>Your name (first and last):</label>
                        <input placeholder="Name" value={name} onChange={ (e) => setName(e.target.value)}/>
                    </div>

                    <div className={isEmailFilled?"required field" : "error field"}>
                        <label>Your email address:</label>
                        <Input iconPosition='left'>
                            <Icon name='at' />
                            <input placeholder="Email" value={email} onChange={ (e) => setEmail(e.target.value)}/>
                        </Input>
                    </div>
                </div>
                
                <h2>Organism Details</h2>
                <div className="equal width fields">
                    <AntigenTypeDropdown />

                    <div className={isSerotypeFilled?"required field" : "error field"}>
                        <label>Serotype:</label>
                        <input placeholder="e.g. K3" value={serotype} onChange={ (e) => setSerotype(e.target.value)}/>
                    </div>
                </div>

                <div className="equal width fields">
                    <div className="field">
                        <label>NMR:</label>
                        <TextArea placeholder="Available NMR data (H-1 and other diagnostic proton chemical shifts in ppm)" value={NMR} onChange={ (e) => setNMR(e.target.value)}/>
                    </div>

                    <div className="field">
                        <label>Species where found:</label>
                        <input placeholder="Species" value={speciesWhereFound} onChange={ (e) => setSpeciesWhereFound(e.target.value)}/>
                    </div>
                </div>
                
                <div className="equal width fields">
                    <div className="field">
                        <label>Molecular type:</label>
                        <input placeholder="Molecular type" value={molecularType} onChange={ (e) => setMolecularType(e.target.value)}/>
                    </div>

                    <div className="field">
                        <label>Branched?</label>
                        <div className="ui radio checkbox" style={{marginLeft:"1vh", marginRight:"4vh"}}>
                            <input type="radio" name="branched" value="1" checked={branched==='1'} onChange={ (e) => setBranched(e.target.value)}/>
                            <label>Yes</label>
                        </div>

                        <div className="ui radio checkbox" style={{marginLeft:"1vh", marginRight:"4vh"}}>
                            <input type="radio" name="branched" value="0" checked={branched==='0'} onChange={ (e) => setBranched(e.target.value)}/>
                            <label>No</label>
                        </div>

                        <div className="ui radio checkbox">
                            <input type="radio" name="branched" value='' checked={branched===''} onChange={ (e) => setBranched(e.target.value)}/>
                            <label>Undocumented</label>
                        </div>
                    </div>
                </div>

                <div className="equal width fields">
                    <div className="field">
                        <Counter label="Repeated Unit Size:" repeatedUnits={repeatedUnits} setRepeatedUnits={setRepeatedUnits} defaultMsg='Unknown' max={100}/>
                    </div>

                    <div className={isDOIFilled?"required field" : "error field"}>
                        <label>Scientific literature source:</label>
                        <input placeholder="https://www..." value={doi} onChange={ (e) => setDOI(e.target.value)}/>
                    </div>
                </div>

                <div className="equal width fields">
                    <div className={isCASPERFilled?"required field" : "error field"} style={{display:'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <label style={{ alignSelf: 'flex-start' }}>Chemical structure (in CASPER format):</label>
                        <input placeholder="CASPER format" value={casper} onChange={handleCASPERInputChange}/>
                        <p style={{ alignSelf: 'flex-start' }}>For special characters, use <strong>\a for α, \b for β, -{'>'} for → </strong></p>
                    </div>


                    <div className="field">
                        <label>References:</label>
                        <input placeholder="Separated by commas" value={refs} onChange={ (e) => setRefs(e.target.value)}/>
                    </div>
                </div>

                <div className="equal width fields">
                    

                    <div className="field">
                        <div style={{maxWidth:'30%',marginTop:'1vh', borderColor:'black', borderWidth:'0.05vh',borderStyle: 'solid', padding:'1vh', borderRadius:'2vh'}}>
                            <h4>2D Representation Preview</h4>
                            <AutoSVG casperString={casper}/>

                            {carbuilderFormat !== '' &&
                                <div className="field" style={{display:'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <label style={{ alignSelf: 'flex-start' }}>Carbuilder format (automatically generated):</label>
                                    <p>{carbuilderFormat}</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className="field" style={{display:"flex", alignItems:"flex-end", justifyContent:"flex-end"}}>
                    { formMessage }
                    <button className="ui large button" style={{marginLeft:"2vh"}} type="submit" onClick={handleSubmit}>Submit</button>
                </div>

                
            </form>

            <ConfirmationModal
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={axiosPostData}
                message={`Are you sure you want to submit this suggestion?`}
            />

        </div>
        
    )
}